/**
 * Brand Logo Component
 *
 * Use this component instead of hardcoding logo paths.
 * Logo paths are controlled from src/config/branding.ts
 */

import { branding } from "../config/branding";
import { useTheme } from "../context/ThemeContext";

interface BrandLogoProps {
  /** Logo variant: 'primary' for main logo, 'wordmark' for text logo */
  variant?: "primary" | "wordmark";
  /** Additional CSS classes */
  className?: string;
  /** Height (CSS value or number for pixels) */
  height?: number | string;
  /** Width (CSS value or number for pixels) */
  width?: number | string;
}

export function BrandLogo({
  variant = "primary",
  className = "",
  height,
  width,
}: BrandLogoProps) {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const src = variant === "primary"
    ? branding.logos.primary
    : branding.logos.wordmark;

  const alt = `${branding.company.name} Logo`;

  const style: React.CSSProperties = {};
  if (height !== undefined) {
    style.height = typeof height === "number" ? `${height}px` : height;
  }
  if (width !== undefined) {
    style.width = typeof width === "number" ? `${width}px` : width;
  }

  // Invert logo to black on light theme (logo is white by default)
  if (isLight) {
    style.filter = "brightness(0)";
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={Object.keys(style).length > 0 ? style : undefined}
    />
  );
}

/** Company name from branding config */
export const companyName = branding.company.name;

/** Company tagline from branding config */
export const companyTagline = branding.company.tagline;

/** Full branding config export for advanced usage */
export { branding } from "../config/branding";
